import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { OverlayFooter, OverlayHeader, OverlayPanel } from '../OverlayPanel';
import { DateRangePicker, DateRangePreset } from '../DateRangePicker';
import { HAS_VALUE_SENTINEL, NO_VALUE_SENTINEL } from '../data-lists/customFieldFilterParam';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { countActive, FilterField, FilterOption, FilterValues, isActive, SortOption } from './listFilters';

export interface CustomFieldsConfig {
  /** Extra fields (custom columns / data-list fields) the list can be filtered by. */
  fields: Array<{ key: string; label: string; hasValueOnly?: boolean }>;
  loadValues: (key: string) => Promise<Array<string | FilterOption>>;
}

interface Props {
  visible: boolean;
  title: string;
  fields: FilterField[];
  sorts?: SortOption[];
  value: { filters: FilterValues; sort?: string; customFields: Record<string, string[]> };
  customFields?: CustomFieldsConfig;
  onApply: (value: { filters: FilterValues; sort?: string; customFields: Record<string, string[]> }) => void;
  onClose: () => void;
}

const toIso = (d?: Date) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined);
const fromIso = (s?: string) => (s ? new Date(`${s}T00:00:00`) : undefined);
const numberOf = (text: string) => { const cleaned = text.replace(/[^0-9.]/g, ''); const n = parseFloat(cleaned); return { cleaned, value: Number.isFinite(n) ? n : undefined }; };

/** Full-screen sort & filter for a list: draft edited here, applied on "Show results". */
export const FilterSheet: React.FC<Props> = ({ visible, title, fields, sorts = [], value, customFields, onApply, onClose }) => {
  const styles = useThemedStyles(getStyles);
  const insets = useSafeAreaInsets();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [draft, setDraft] = useState(value);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [openField, setOpenField] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setDraft({ filters: { ...value.filters }, sort: value.sort, customFields: { ...value.customFields } });
    const next: Record<string, string> = {};
    for (const f of fields) if (f.type === 'range') {
      const r = value.filters[f.key] || {};
      next[`${f.key}.min`] = r.min !== undefined ? String(r.min) : '';
      next[`${f.key}.max`] = r.max !== undefined ? String(r.max) : '';
    }
    setTexts(next);
  }, [visible]);

  const set = (key: string, v: any) => setDraft((d) => {
    const filters = { ...d.filters };
    if (v === undefined || (Array.isArray(v) && !v.length)) delete filters[key]; else filters[key] = v;
    return { ...d, filters };
  });
  const activeCount = countActive(fields, draft);

  const chip = (active: boolean, label: string, onPress: () => void, color?: string, icon?: string) => (
    <TouchableOpacity key={label} style={[styles.chip, { flexDirection: rtl.rowDirection }, active && { backgroundColor: color || (colors.primary as string), borderColor: color || (colors.primary as string) }]} onPress={onPress} activeOpacity={0.7}>
      {!!icon && <Ionicons name={icon as any} size={14} color={active ? (colors.white as string) : (colors.text.secondary as string)} />}
      <Text style={[styles.chipText, active && styles.chipTextActive]} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
  );

  const renderField = (f: FilterField) => {
    const v = draft.filters[f.key];
    switch (f.type) {
      case 'multi': {
        const list: string[] = v || [];
        return <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
          {f.options.map((o) => chip(list.includes(o.value), o.label, () => set(f.key, list.includes(o.value) ? list.filter((x) => x !== o.value) : [...list, o.value]), o.color))}
        </View>;
      }
      case 'choice':
        return <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
          {f.options.map((o) => chip(v === o.value, o.label, () => set(f.key, v === o.value ? undefined : o.value), o.color))}
        </View>;
      case 'range': {
        const input = (side: 'min' | 'max') => (
          <TextInput
            style={styles.input}
            value={texts[`${f.key}.${side}`] || ''}
            onChangeText={(text) => {
              const n = numberOf(text);
              setTexts((p) => ({ ...p, [`${f.key}.${side}`]: n.cleaned }));
              const next = { ...(draft.filters[f.key] || {}), [side]: n.value };
              set(f.key, next.min === undefined && next.max === undefined ? undefined : next);
            }}
            keyboardType="decimal-pad"
            placeholder={side === 'min' ? t('listFilters.min') : t('listFilters.max')}
            placeholderTextColor={colors.text.disabled as string}
          />
        );
        return <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>{input('min')}<Text style={styles.dash}>–</Text>{input('max')}{!!f.unit && <Text style={styles.unit}>{f.unit}</Text>}</View>;
      }
      case 'date':
        return <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
          <DateRangePicker
            preset={(v?.preset as DateRangePreset) || 'custom'}
            startDate={fromIso(v?.from)}
            endDate={fromIso(v?.to)}
            onChange={(preset, from, to) => set(f.key, from || to ? { preset, from: toIso(from), to: toIso(to) } : undefined)}
          />
          {isActive(f, v) && <TouchableOpacity onPress={() => set(f.key, undefined)}><Text style={styles.link}>{t('listFilters.anyDate')}</Text></TouchableOpacity>}
        </View>;
      case 'text':
        return <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={v || ''} onChangeText={(text) => set(f.key, text || undefined)} placeholder={f.placeholder} placeholderTextColor={colors.text.disabled as string} />;
      case 'entity':
        return <EntityPicker field={f} value={v || []} onChange={(next) => set(f.key, next)} />;
    }
  };

  // Fields grouped by section, in their declared order.
  const groups = useMemo(() => {
    const out: Array<{ section?: string; fields: FilterField[] }> = [];
    for (const f of fields) {
      const last = out[out.length - 1];
      if (last && last.section === f.section) last.fields.push(f); else out.push({ section: f.section, fields: [f] });
    }
    return out;
  }, [fields]);

  return (
    <OverlayPanel visible={visible} onClose={onClose}>
      <View style={[styles.page, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
        <OverlayHeader icon="options-outline" title={title} onClose={onClose} right={(
          <TouchableOpacity onPress={() => { setDraft({ filters: {}, sort: undefined, customFields: {} }); setTexts({}); }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={styles.reset}>{t('listFilters.reset')}</Text>
          </TouchableOpacity>
        )} />

        <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent} keyboardShouldPersistTaps="handled">
          {sorts.length > 0 && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('listFilters.sortBy')}</Text>
              <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
                {sorts.map((s, i) => chip((draft.sort || sorts[0].key) === s.key, s.label, () => setDraft((d) => ({ ...d, sort: i === 0 ? undefined : s.key })), undefined, s.icon))}
              </View>
            </View>
          )}

          {groups.map((g, gi) => (
            <View key={gi} style={styles.group}>
              {!!g.section && <Text style={[styles.groupTitle, { textAlign: rtl.textStart }]}>{g.section}</Text>}
              {g.fields.map((f) => (
                <View key={f.key} style={styles.section}>
                  <View style={[styles.fieldHead, { flexDirection: rtl.rowDirection }]}>
                    <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{f.label}</Text>
                    {isActive(f, draft.filters[f.key]) && <TouchableOpacity onPress={() => { set(f.key, undefined); if (f.type === 'range') setTexts((p) => ({ ...p, [`${f.key}.min`]: '', [`${f.key}.max`]: '' })); }}>
                      <Text style={styles.link}>{t('listFilters.clear')}</Text>
                    </TouchableOpacity>}
                  </View>
                  {!!f.hint && <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{f.hint}</Text>}
                  {renderField(f)}
                </View>
              ))}
            </View>
          ))}

          {!!customFields?.fields.length && (
            <View style={styles.group}>
              <Text style={[styles.groupTitle, { textAlign: rtl.textStart }]}>{t('listFilters.moreFields')}</Text>
              {customFields.fields.map((cf) => (
                <CustomFieldRow
                  key={cf.key}
                  field={cf}
                  open={openField === cf.key}
                  onToggle={() => setOpenField(openField === cf.key ? null : cf.key)}
                  value={draft.customFields[cf.key] || []}
                  loadValues={customFields.loadValues}
                  onChange={(next) => setDraft((d) => {
                    const cfs = { ...d.customFields };
                    if (next.length) cfs[cf.key] = next; else delete cfs[cf.key];
                    return { ...d, customFields: cfs };
                  })}
                />
              ))}
            </View>
          )}
        </ScrollView>

        <OverlayFooter>
          <TouchableOpacity style={styles.apply} onPress={() => { onApply(draft); onClose(); }}>
            <Text style={styles.applyText}>{activeCount ? t('listFilters.applyWithCount', { count: activeCount }) : t('listFilters.apply')}</Text>
          </TouchableOpacity>
        </OverlayFooter>
      </View>
    </OverlayPanel>
  );
};

/** Search-and-pick records (vendors, customers, categories…). */
const EntityPicker: React.FC<{ field: Extract<FilterField, { type: 'entity' }>; value: FilterOption[]; onChange: (v: FilterOption[]) => void }> = ({ field, value, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const n = ++seq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try { const r = await field.search(query.trim()); if (n === seq.current) setResults(r.slice(0, 12)); } catch { if (n === seq.current) setResults([]); }
      finally { if (n === seq.current) setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);
  const selected = new Set(value.map((o) => o.value));
  return (
    <View style={styles.stack}>
      {value.length > 0 && <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
        {value.map((o) => (
          <TouchableOpacity key={o.value} style={[styles.chip, styles.chipSelected, { flexDirection: rtl.rowDirection }]} onPress={() => onChange(value.filter((x) => x.value !== o.value))}>
            <Text style={styles.chipTextActive} numberOfLines={1}>{o.label}</Text>
            <Ionicons name="close" size={14} color={colors.white as string} />
          </TouchableOpacity>
        ))}
      </View>}
      <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="search" size={16} color={colors.text.secondary as string} />
        <TextInput style={[styles.searchInput, { textAlign: rtl.textStart }]} value={query} onChangeText={setQuery} placeholder={field.placeholder || t('listFilters.searchToAdd')} placeholderTextColor={colors.text.disabled as string} />
        {loading && <ActivityIndicator size="small" color={colors.primary as string} />}
      </View>
      <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
        {results.filter((o) => !selected.has(o.value)).map((o) => (
          <TouchableOpacity key={o.value} style={[styles.chip, { flexDirection: rtl.rowDirection }]} onPress={() => onChange([...value, o])}>
            <Ionicons name="add" size={14} color={colors.primary as string} />
            <Text style={styles.chipText} numberOfLines={1}>{o.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

/** One extra field: has a value / no value, or pick from its values. */
const CustomFieldRow: React.FC<{ field: { key: string; label: string; hasValueOnly?: boolean }; open: boolean; onToggle: () => void; value: string[]; loadValues: CustomFieldsConfig['loadValues']; onChange: (v: string[]) => void }> = ({ field, open, onToggle, value, loadValues, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const [options, setOptions] = useState<FilterOption[] | null>(null);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open || options || field.hasValueOnly) return;
    loadValues(field.key).then((vals) => setOptions(vals.map((x) => (typeof x === 'string' ? { value: x, label: x } : x)))).catch(() => setOptions([]));
  }, [open]);
  const summary = value.includes(HAS_VALUE_SENTINEL) ? t('listFilters.hasValue') : value.includes(NO_VALUE_SENTINEL) ? t('listFilters.noValue')
    : value.map((v) => options?.find((o) => o.value === v)?.label || v).join(', ');
  const toggleValue = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value.filter((x) => x !== HAS_VALUE_SENTINEL && x !== NO_VALUE_SENTINEL), v]);
  const shown = (options || []).filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 40);
  return (
    <View style={styles.cfRow}>
      <TouchableOpacity style={[styles.cfHead, { flexDirection: rtl.rowDirection }]} onPress={onToggle}>
        <Text style={[styles.cfLabel, { textAlign: rtl.textStart }]} numberOfLines={1}>{field.label}</Text>
        {!!value.length && <Text style={styles.cfSummary} numberOfLines={1}>{summary}</Text>}
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary as string} />
      </TouchableOpacity>
      {open && (
        <View style={styles.stack}>
          <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
            {[[HAS_VALUE_SENTINEL, t('listFilters.hasValue')], [NO_VALUE_SENTINEL, t('listFilters.noValue')]].map(([v, label]) => (
              <TouchableOpacity key={v} style={[styles.chip, value.includes(v) && styles.chipSelected]} onPress={() => onChange(value.includes(v) ? [] : [v])}>
                <Text style={[styles.chipText, value.includes(v) && styles.chipTextActive]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {!field.hasValueOnly && (options === null ? <ActivityIndicator size="small" color={colors.primary as string} /> : (
            <>
              {options.length > 8 && <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={search} onChangeText={setSearch} placeholder={t('listFilters.searchValues')} placeholderTextColor={colors.text.disabled as string} />}
              <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
                {shown.map((o) => (
                  <TouchableOpacity key={o.value} style={[styles.chip, value.includes(o.value) && styles.chipSelected]} onPress={() => toggleValue(o.value)}>
                    <Text style={[styles.chipText, value.includes(o.value) && styles.chipTextActive]} numberOfLines={1}>{o.label}</Text>
                  </TouchableOpacity>
                ))}
                {!options.length && <Text style={styles.hint}>{t('listFilters.noValues')}</Text>}
              </View>
            </>
          ))}
        </View>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  page: { flex: 1, backgroundColor: colors.background.surface, ...(Platform.OS === 'web' && { maxWidth: 640, alignSelf: 'center' as const, width: '100%' as const, backgroundColor: colors.background.surfaceRaised }) },
  header: { alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingVertical: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border, gap: spacing.md },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.text.primary },
  reset: { fontSize: typography.fontSize.sm, color: colors.primary, fontWeight: '600' },
  body: { flex: 1 },
  bodyContent: { paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.xl },
  group: { marginBottom: spacing.sm },
  groupTitle: { fontSize: typography.fontSize.xs, fontWeight: '800', color: colors.text.disabled, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: spacing.sm, marginTop: spacing.xs },
  section: { marginBottom: spacing.lg },
  fieldHead: { alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  sectionTitle: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.text.secondary },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.disabled, marginBottom: spacing.xs },
  chipsRow: { gap: spacing.sm, flexWrap: 'wrap' },
  chip: { alignItems: 'center', gap: 6, maxWidth: 260, paddingVertical: spacing.xs + 2, paddingHorizontal: spacing.md, borderRadius: 999, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  chipSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600' },
  chipTextActive: { color: colors.white, fontSize: typography.fontSize.sm, fontWeight: '600' },
  row: { alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
  stack: { gap: spacing.sm },
  input: { flex: 1, minWidth: 90, borderWidth: 1, borderColor: colors.border, borderRadius: spacing.borderRadius.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, fontSize: typography.fontSize.base, color: colors.text.primary, backgroundColor: colors.background.surfaceRaised },
  dash: { color: colors.text.secondary },
  unit: { color: colors.text.secondary, fontSize: typography.fontSize.sm },
  link: { fontSize: typography.fontSize.xs, color: colors.primary, fontWeight: '600' },
  searchBox: { alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: spacing.borderRadius.sm, paddingHorizontal: spacing.sm, backgroundColor: colors.background.surfaceRaised },
  searchInput: { flex: 1, paddingVertical: spacing.sm, fontSize: typography.fontSize.base, color: colors.text.primary },
  cfRow: { borderTopWidth: 1, borderTopColor: colors.border, paddingVertical: spacing.sm, gap: spacing.sm },
  cfHead: { alignItems: 'center', gap: spacing.sm },
  cfLabel: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600' },
  cfSummary: { maxWidth: 160, fontSize: typography.fontSize.xs, color: colors.primary },
  footer: { padding: spacing.lg, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.background.surface },
  apply: { backgroundColor: colors.primary, borderRadius: spacing.borderRadius.md, paddingVertical: spacing.md, alignItems: 'center' },
  applyText: { color: colors.white, fontSize: typography.fontSize.base, fontWeight: '700' },
});
